import { redirect } from '@tanstack/react-router';
import { createServerFn } from '@tanstack/react-start';
import { getRequest, setResponseHeaders } from '@tanstack/react-start/server';

import { getServerMessage } from '~/i18n/server-message.server.ts';
import { createEmailAuthAccount, getUserByEmail, signInWithEmail } from '~/modules/auth/service.server.ts';
import SignUpFormSchema from '~/modules/auth/sign-up-form-schema.ts';
import { safeRedirect } from '~/utils/http.server.ts';

import { createServerFnObservabilityMiddleware } from '../utils/create-instrumented-server-fn.ts';
import { commitAuthSession } from './session.server.ts';
import type { AuthMutationResult } from './sign-in-with-email.ts';

export const signUpWithEmailMutation = createServerFn({ method: 'POST' })
	.middleware([
		createServerFnObservabilityMiddleware({
			dependencies: ['auth.user.lookup', 'auth.sign-up', 'auth.sign-in', 'auth.session.cookie-write'],
			relatesTo: 'auth.sign-up-email',
		}),
	])
	.inputValidator(SignUpFormSchema)
	.handler(async ({ data }) => {
		const request = getRequest();
		const existingUser = await getUserByEmail(data.email);

		if (existingUser) {
			return {
				formError: getServerMessage(request, 'serverError.signUp'),
				status: 'error',
			} satisfies AuthMutationResult;
		}

		const user = await createEmailAuthAccount(data.email, data.password);
		if (!user) {
			return {
				formError: getServerMessage(request, 'serverError.signUp'),
				status: 'error',
			} satisfies AuthMutationResult;
		}

		const authSession = await signInWithEmail(data.email, data.password);
		if (!authSession) {
			return {
				formError: getServerMessage(request, 'serverError.signUp'),
				status: 'error',
			} satisfies AuthMutationResult;
		}

		setResponseHeaders(
			new Headers({
				'Set-Cookie': await commitAuthSession(request, {
					authSession,
					flashErrorMessage: null,
				}),
			}),
		);

		throw redirect({
			href: safeRedirect(data.redirectTo, '/dashboard'),
		});
	});
